/* eslint-disable import/extensions */
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import {
  Col,
  Row,
  Button,
  Card,
  CardBody,
  Nav,
  NavItem,
  FormGroup,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { NavLink } from 'react-router-dom';
import { ValidationError } from 'yup';

import iconBoxP from '~/assets/icons/icone-caixa-p.svg';
import iconBoxM from '~/assets/icons/icone-caixa-m.svg';
import iconBoxG from '~/assets/icons/icone-caixa-g.svg';
import iconBoxCustom from '~/assets/icons/icone-caixa-personalizada.svg';
import dimensionsImg from '~/assets/icons/correios-dimensions-illustration.svg';
import iconCardSelected from '~/assets/icons/icone-card-selected.svg';
import iconTextBox from '~/assets/icons/icone-caixa-frase.svg';

import { Form } from '@unform/web';
import {
  updateDelivery,
  updateClientAddress,
  saveClientAddress,
  setTemporaryStore,
  updateVehicleForDelivery,
  createCart,
  updateCartInformations,
  updateCorreiosPackage,
  setDeliveryFleetRequest,
} from '~/store/modules/cart/actions';

import api from '~/services/api';
import AddressForm from '~/components/AddressForm';
import SchemaValidator from '~/validators/form/sale_delivery_form';
import { deliveryFleets } from '~/util/deliveryFleets';

import Loading from '~/components/Loading';
import InputSelect from '~/components/Inputs/InputSelect';
import { getRole } from '~/util/getRole';
import DefaultStoreAlert from '../DefaultStoreAlert';

const VEHICLE_LABELS = {
  M: 'Moto',
  M_PRO: 'Moto Baú',
  C: 'Carro',
  UV_FIORINO: 'Pick-Up',
};

const DeliveryForm = ({ targetUserId }) => {
  const dispatch = useDispatch();

  const formRef = useRef(null);
  const formStoreRef = useRef(null);
  const formVehicleRef = useRef(null);
  const deliveryObservationRef = useRef(null);

  const userId = useSelector(({ user }) => user.profile.id);
  const roles = useSelector(({ user }) => user.profile.roles);

  const role = useMemo(
    () =>
      getRole(roles, [
        'administrator',
        'digital-consultant',
        'cross-manager',
        'salesman',
        'store-manager',
        'financier',
      ]),
    [roles]
  );

  const [handleDisabled, setHandleDisabled] = useState(false);

  const vehicle = useSelector(({ cart }) => cart.vehicle);

  const deliveryTag = useSelector(({ cart }) => cart.deliveryFleet);

  const deliveryMethod = useSelector(({ cart }) => cart.cart?.delivery_method);

  const [deliveryServices, setDeliveryServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const [hasCorreiosEnabled, setHasCorreiosEnabled] = useState(false);

  // Modalidades habilitadas na configuração delivery_types do subscriber.
  // Default tudo habilitado: em caso de erro na request, comporta-se
  // como antes da feature (mesmo fallback do backend).
  const [enabledDeliveryTypes, setEnabledDeliveryTypes] = useState({
    delivery: true,
    store_pickup: true,
    correios: true,
    malinha: true,
  });

  const PACKAGES = {
    P: {
      label: 'Caixa P (M08)',
      width: 24,
      length: 16,
      height: 8,
      weight: 0.56,
      maxWeightLabel: 'até 0,56 kg',
      description: 'cosméticos, perfumes, pequenos acessórios etc.',
      icon: iconBoxP,
    },
    M: {
      label: 'Caixa M (G12)',
      width: 32,
      length: 24,
      height: 12,
      weight: 1.88,
      maxWeightLabel: 'até 1,88 kg',
      description: 'caixas de sapato, camisas, calças e peças de vestuário.',
      icon: iconBoxM,
    },
    G: {
      label: 'Caixa G (S24)',
      width: 48,
      length: 32,
      height: 24,
      weight: 3.0,
      maxWeightLabel: 'até 3 kg',
      description: 'múltiplas unidades, itens grandes ou volumosos.',
      icon: iconBoxG,
    },
    CUSTOM: {
      label: 'Personalizada',
      maxWeightLabel: 'até -- kg',
      description: 'medidas especiais ou produtos fora dos tamanhos P, M ou G.',
      icon: iconBoxCustom,
    },
  };

  const [packageType, setPackageType] = useState(null);
  const [dimensions, setDimensions] = useState({
    width: '',
    length: '',
    height: '',
    weight: '',
  });

  const [dimensionErrors, setDimensionErrors] = useState({
    width: '',
    length: '',
    height: '',
    weight: '',
  });

  const correiosPackage = useSelector(({ cart }) => cart.correios_package);

  const handleSelectPackage = type => {
    setPackageType(type);

    if (type === 'CUSTOM') {
      setDimensions({
        width: '',
        length: '',
        height: '',
        weight: '',
      });

      dispatch(
        updateCorreiosPackage({
          type: 'CUSTOM',
          width: null,
          length: null,
          height: null,
          weight: null,
          box_name: 'Pacote Personalizado',
        })
      );

      return;
    }

    const data = PACKAGES[type];

    setDimensions({
      width: data.width,
      length: data.length,
      height: data.height,
      weight: data.weight,
    });

    dispatch(
      updateCorreiosPackage({
        type,
        width: data.width,
        length: data.length,
        height: data.height,
        weight: data.weight,
        box_name: data.label,
      })
    );
  };

  const validateDimensions = dims => {
    const errors = {};

    const width = Number(dims.width);
    const length = Number(dims.length);
    const height = Number(dims.height);
    const weight = Number(dims.weight);

    if (!width) errors.width = 'Informe a largura!';
    if (!length) errors.length = 'Informe o comprimento!';
    if (!height) errors.height = 'Informe a altura!';
    if (!weight) errors.weight = 'Informe o peso!';

    if (!width || !length || !height || !weight) {
      return errors;
    }
    // limites Correios (caixa PAC/SEDEX padrão)
    if (width < 10 || width > 100) {
      errors.width = 'Largura deve ser entre 10 e 100 cm.';
    }

    if (length < 15 || length > 100) {
      errors.length = 'Comprimento deve ser entre 15 e 100 cm.';
    }

    if (height < 1 || height > 100) {
      errors.height = 'Altura deve ser entre 1 e 100 cm.';
    }

    const sum = width + length + height;
    if (sum < 26 || sum > 200) {
      const msg = 'A soma L+C+A deve ser entre 26 cm e 200 cm.';
      if (!errors.width) errors.width = msg;
      if (!errors.length) errors.length = msg;
      if (!errors.height) errors.height = msg;
    }

    if (weight <= 0 || weight > 30) {
      errors.weight = 'Peso deve ser maior que 0 e até 30 kg.';
    }

    return errors;
  };

  const [showDefaultStoreAlert, setShowDefaultStoreAlert] = useState(false);

  const selectedStore = useSelector(({ cart }) => cart.selectedStore);

  const userStore = useSelector(({ user }) => ({
    value: user?.profile?.stock?.id,
    label: user?.profile?.stock?.fantasy_name,
    has_mapp: user?.profile?.stock?.has_mapp,
  }));

  // if user is store manager we dont pull the data from the profile, we load from a request
  const [managedStores, setManagedStores] = useState([]);

  const routerEnabled = useMemo(
    () => Boolean(selectedStore?.delivery_router_enabled),
    [selectedStore]
  );

  const routerDefaultOperator = useMemo(
    () => selectedStore?.delivery_router_default_operator || null,
    [selectedStore]
  );

  const vehiclesAvailable = useMemo(() => {
    if (routerEnabled) {
      const configured = selectedStore?.delivery_router_default_vehicles || [];

      return configured.map(code => ({
        value: code,
        label: VEHICLE_LABELS[code] || code,
      }));
    }

    return [
      { value: 'M', label: 'Moto' },
      ...(deliveryTag &&
      ['UBER', 'LALAMOVE', 'ENTREGA99', 'IFOOD_ENVIOS'].includes(deliveryTag)
        ? [{ value: 'C', label: 'Carro' }]
        : []),
      ...(deliveryTag === 'LALAMOVE'
        ? [
            { value: 'M_PRO', label: 'Moto Baú' },
            { value: 'UV_FIORINO', label: 'Pick-Up' },
          ]
        : []),
    ];
  }, [routerEnabled, selectedStore, deliveryTag]);

  const [storesAvailable, setStoresAvailable] = useState([]);

  const storeSelectorStyles = {
    menu: base => ({
      ...base,
      maxHeight: 250,
      width: '100%',
    }),
    menuList: base => ({
      ...base,
      maxHeight: 250,
      width: '100%',
    }),
  };

  const setStoreSelectable = option => {
    const store = managedStores.find(p => p.value === option.value);

    if (!store) return;

    const selectStore = {
      value: store.value,
      label: store.label,
      has_mapp: store.has_mapp,
      delivery_router_enabled: store.delivery_router_enabled,
      delivery_router_default_operator: store.delivery_router_default_operator,
      delivery_router_default_vehicles: store.delivery_router_default_vehicles,
    };

    dispatch(setTemporaryStore(selectStore));
  };

  const done = useSelector(({ cart }) => cart.done);
  const cartState = useSelector(({ cart }) => cart.cart);

  const [deliveryObservation, setDeliveryObservation] = useState();
  const is_suitcase_delivery = useSelector(
    ({ cart }) => cart.cart?.is_suitcase_delivery || cart.is_suitcase_delivery
  );

  const [deliveryType, setDeliveryType] = useState(null);

  const [selectedDelivery, setSelectedDelivery] = useState(null);

  // iFood Envios não faz coleta no cliente: fora da Malinha.
  const deliveryServiceOptions = useMemo(
    () =>
      deliveryType === 'malinha'
        ? deliveryServices.filter(s => s.tag !== 'IFOOD_ENVIOS')
        : deliveryServices,
    [deliveryServices, deliveryType]
  );

  useEffect(() => {
    if (!cartState) return;

    let defaultDeliveryType;

    if (deliveryMethod === 'CORREIOS') {
      defaultDeliveryType = 'correios';

      setSelectedDelivery('CORREIOS');

      dispatch(
        updateDelivery({
          tag: 'CORREIOS',
          value: true,
          is_suitcase_delivery: false,
        })
      );
    } else if (cartState?.is_for_delivery && !is_suitcase_delivery) {
      defaultDeliveryType = 'entrega';
      dispatch(
        updateDelivery({
          value: true,
          is_suitcase_delivery: false,
        })
      );
    } else if (cartState?.is_for_delivery && is_suitcase_delivery) {
      defaultDeliveryType = 'malinha';
      dispatch(
        updateDelivery({
          value: true,
          is_suitcase_delivery: true,
        })
      );
    } else {
      defaultDeliveryType = 'retirada';
      dispatch(
        updateDelivery({
          tag: 'RETIRAR',
          value: false,
          is_suitcase_delivery: false,
        })
      );
    }

    setDeliveryType(defaultDeliveryType);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!cartState) return;

    if (deliveryTag === 'CORREIOS') {
      setDeliveryType('correios');
      setSelectedDelivery('CORREIOS');
    }

    if (correiosPackage) {
      const { type, width, length, height, weight } = correiosPackage;

      if (type && PACKAGES[type]) {
        setPackageType(type);

        setDimensions({
          width: width ?? PACKAGES[type].width ?? '',
          length: length ?? PACKAGES[type].length ?? '',
          height: height ?? PACKAGES[type].height ?? '',
          weight: weight ?? PACKAGES[type].weight ?? '',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartState, deliveryTag, correiosPackage]);

  useEffect(() => {
    if (!routerEnabled || !routerDefaultOperator) return;
    if (['retirada', 'correios', 'correios_express'].includes(deliveryType)) {
      return;
    }
    if (deliveryTag === routerDefaultOperator) return;

    // Com o roteador, a transportadora não é escolhida pelo vendedor:
    // assume-se a de maior prioridade da loja, que é a primeira que o
    // sistema vai tentar ao chamar o entregador.
    dispatch(
      updateDelivery({
        tag: routerDefaultOperator,
        value: true,
      })
    );

    if (cartState?.id) {
      dispatch(setDeliveryFleetRequest(routerDefaultOperator));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerEnabled, routerDefaultOperator, deliveryType, cartState?.id]);

  const client = useSelector(({ cart }) => cart.client);

  const chosenRegion = useSelector(({ cart }) => cart.chosenRegion);

  const isForDelivery = deliveryType !== 'retirada';

  const [isFormValid, setIsFormValid] = useState(false);
  const [iDontKnowCEP, setIDontKnowCEP] = useState(null);

  useEffect(() => {
    if (!userStore?.value || selectedStore) return;
    if (!managedStores || managedStores.length === 0) return;

    // A loja default do usuário (userStore) não carrega os campos de
    // roteador. Enriquece a partir de managedStores para que routerEnabled
    // seja detectado já na inicialização, sem depender de troca manual.
    const enriched = managedStores.find(s => s.value === userStore.value);

    dispatch(setTemporaryStore(enriched || userStore));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStore, managedStores]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const getDeliveryServices = async () => {
      try {
        setLoading(true);
        const availableDeliveriesRequest = api.get(
          `/utils/available-delivery-services`
        );
        const availableStoresRequest = api.get(
          `/${role}/users/${targetUserId || userId}/managed_stores`
        );

        const [
          {
            data: { delivery_services, delivery_types },
          },
          {
            data: { managed_stores },
          },
        ] = await Promise.all([
          availableDeliveriesRequest,
          availableStoresRequest,
        ]);

        const enabledTypes = {
          delivery: delivery_types?.delivery !== false,
          store_pickup: delivery_types?.store_pickup !== false,
          correios: delivery_types?.correios !== false,
          malinha: delivery_types?.malinha !== false,
        };

        setEnabledDeliveryTypes(enabledTypes);

        setManagedStores(
          managed_stores.map(s => ({
            value: s.id,
            label: s.fantasy_name,
            has_mapp: s.has_mapp,
            delivery_router_enabled: s.delivery_router_enabled,
            delivery_router_default_operator:
              s.delivery_router_default_operator,
            delivery_router_default_vehicles:
              s.delivery_router_default_vehicles,
          }))
        );

        const correiosEnabled = Array.isArray(delivery_services)
          ? delivery_services.includes('CORREIOS')
          : false;

        // Botão Correios: integração configurada E modalidade habilitada
        setHasCorreiosEnabled(correiosEnabled && enabledTypes.correios);

        const getDeliveryServicesAvailable = deliveryFleets.filter(type => {
          if (type.tag === 'RETIRAR') return true;
          if (type.tag === 'CORREIOS') return false;
          return delivery_services.includes(type.tag);
        });

        const getDeliveryServicesAvailableWithSubscriber =
          getDeliveryServicesAvailable.map(type =>
            type.tag === 'PROPRIA'
              ? {
                  ...type,
                  label: `Alternativa`,
                  value: type.tag,
                  is_for_delivery: type.value,
                }
              : { ...type, value: type.tag, is_for_delivery: type.value }
          );

        setDeliveryServices(getDeliveryServicesAvailableWithSubscriber);
        setLoading(false);
      } catch (err) {
        // console.log(err);
        if (err.response && err?.response?.data?.message) {
          toast.error(err?.response?.data?.message);
        } else {
          toast.error('Erro ao carregar os serviços de entrega!');
        }
        setLoading(false);
      }
    };

    getDeliveryServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!managedStores || managedStores.length === 0) {
      setStoresAvailable([]);
      return;
    }

    if (deliveryTag === 'PROPRIA') {
      if (!selectedStore?.has_mapp) {
        dispatch(setTemporaryStore(''));
      }
      setStoresAvailable(managedStores.filter(s => s.has_mapp));
      return;
    }

    if (deliveryType) {
      setStoresAvailable(managedStores);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managedStores, deliveryTag, deliveryType]);

  const updateCartAction = useCallback(() => {
    if (!cartState?.id) {
      return createCart({
        setHandleDisabled,
        delivery_observation: deliveryObservation,
        target_user_id: targetUserId,
      });
    }
    return updateCartInformations({
      setHandleDisabled,
      is_suitcase_delivery: deliveryType === 'malinha',
      delivery_observation: deliveryObservation,
      target_user_id: targetUserId,
    });
  }, [cartState?.id, deliveryObservation, deliveryType, targetUserId]);

  useEffect(() => {
    if (!isFormValid) {
      return;
    }

    dispatch(updateCartAction());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormValid]);

  const btnLabel = useMemo(() => {
    if (cartState && cartState?.id) {
      return 'Atualizar Carrinho';
    }
    return 'Avançar';
  }, [cartState]);

  const handleSubmitAddress = async data => {
    setHandleDisabled(true);
    try {
      formRef.current.setErrors({});
      await SchemaValidator.validate(data, {
        abortEarly: false,
      });

      setIsFormValid(true);

      data = {
        ...data,
        delivery_additional_data: chosenRegion
          ? { region_id: chosenRegion.id, name: chosenRegion.name }
          : null,
      };

      dispatch(
        cartState?.id
          ? updateClientAddress(data)
          : saveClientAddress({ address: data })
      );
    } catch (err) {
      const validationErrors = {};

      if (err instanceof ValidationError) {
        err.inner.forEach(e => {
          validationErrors[e.path] = e.message;
        });

        if (iDontKnowCEP) {
          validationErrors.cep = undefined;
        }

        formRef.current.setErrors(validationErrors);
      }

      if (err.response && err?.response?.data?.message) {
        toast.error(err?.response?.data?.message, {
          autoClose: err?.response?.data?.duration || 5000,
        });
      }
    } finally {
      setHandleDisabled(false);
    }
  };

  const handleSubmit = async _ => {
    setHandleDisabled(true);

    if (!selectedStore) {
      toast.error('Selecione uma loja para continuar!');
      formStoreRef.current.setFieldError('store_id', 'Selecione uma loja');
      setHandleDisabled(false);
      return;
    }

    if (deliveryType === 'malinha' && deliveryTag === 'IFOOD_ENVIOS') {
      toast.error(
        'O iFood Envios não atende a modalidade Malinha. Selecione outra transportadora.'
      );
      setHandleDisabled(false);
      return;
    }

    if (!vehicle && deliveryTag === 'BEE') {
      toast.error('Atenção, existe campos que precisam ser preenchidos!');
      setHandleDisabled(false);
      return;
    }

    if (!vehicle && (deliveryTag === 'BEE' || routerEnabled)) {
      toast.error('Selecione o tipo de veículo para continuar!');
      setHandleDisabled(false);
      return;
    }

    if (deliveryTag === 'RETIRAR') {
      dispatch(updateCartAction());
      setHandleDisabled(false);
      return;
    }

    if (deliveryType === 'correios') {
      if (packageType === 'CUSTOM') {
        const errors = validateDimensions(dimensions);
        setDimensionErrors(errors);

        if (Object.keys(errors).length > 0) {
          toast.error('Revise os campos destacados nas dimensões do pacote.');
          setHandleDisabled(false);
          return;
        }
      } else {
        setDimensionErrors({
          width: '',
          length: '',
          height: '',
          weight: '',
        });
      }

      if (!packageType) {
        toast.error('Selecione uma embalagem!');
        setHandleDisabled(false);
        return;
      }

      const basePayload = {
        type: packageType,
        width: dimensions.width,
        length: dimensions.length,
        height: dimensions.height,
        weight: dimensions.weight,
      };

      let meta = {};

      if (packageType !== 'CUSTOM' && PACKAGES[packageType]) {
        meta = {
          box_name: PACKAGES[packageType].label,
        };
      } else {
        meta = {
          box_name: 'Pacote Personalizado',
        };
      }

      dispatch(
        updateCorreiosPackage({
          ...basePayload,
          ...meta,
        })
      );
    }

    if (!formRef.current) {
      toast.error('Preencha o endereço de entrega para continuar!');
      setHandleDisabled(false);
      return;
    }

    formRef.current.submitForm();
  };

  // auto resize of delivery observation textarea
  useEffect(() => {
    if (!deliveryObservationRef.current) return;

    const deliveryObservationInput = deliveryObservationRef.current;

    deliveryObservationInput.style.height = '0px';
    deliveryObservationInput.style.height = `${deliveryObservationInput.scrollHeight}px`;
  }, [deliveryObservation]);

  return (
    <>
      <Card className="card new-layout">
        <CardBody className="card-body new-layout">
          <Row className="w-100 mx-0 font-size-20 font-weight-500 text-gray-700 mb-3">
            Tipo de Entrega/Coleta
          </Row>
          {loading && (
            <Loading
              customClassName="loadingpix"
              message="Consultando os serviços de entrega!"
            />
          )}
          {!loading && (
            <Row className="w-100 mx-0">
              <Nav className="w-100" pills>
                <Row className="w-100 mx-0">
                  {enabledDeliveryTypes.delivery && (
                    <Col className="px-0 pr-lg-2" xs={12} lg={3}>
                      <NavItem>
                        <NavLink
                          className={`new-layout-nav-pill card d-flex flex-column align-items-center h-fit-content w-100 ${
                            deliveryType === 'entrega' ? 'checked' : 'unchecked'
                          } font-size-14`}
                          onClick={() => {
                            setDeliveryType('entrega');
                            dispatch(
                              updateDelivery({
                                value: true,
                                is_suitcase_delivery: false,
                              })
                            );
                          }}
                          to="#"
                        >
                          <span className="text-nowrap">Entrega</span>
                        </NavLink>
                      </NavItem>
                    </Col>
                  )}
                  {enabledDeliveryTypes.store_pickup && (
                    <Col className="px-0 pr-lg-2" xs={12} lg={3}>
                      <NavItem>
                        <NavLink
                          className={`new-layout-nav-pill card d-flex flex-column align-items-center h-fit-content w-100 ${
                            deliveryType === 'retirada'
                              ? 'checked'
                              : 'unchecked'
                          } font-size-14`}
                          onClick={() => {
                            setDeliveryType('retirada');
                            dispatch(
                              updateDelivery({
                                tag: 'RETIRAR',
                                value: false,
                                is_suitcase_delivery: false,
                              })
                            );
                          }}
                          to="#"
                        >
                          <span className="text-nowrap">Retirada Em Loja</span>
                        </NavLink>
                      </NavItem>
                    </Col>
                  )}
                  {hasCorreiosEnabled && (
                    <Col className="px-0 pr-lg-2" xs={12} lg={3}>
                      <NavItem>
                        <NavLink
                          className={`new-layout-nav-pill card d-flex flex-column align-items-center h-fit-content w-100 ${
                            deliveryType === 'correios'
                              ? 'checked'
                              : 'unchecked'
                          } font-size-14`}
                          onClick={() => {
                            setDeliveryType('correios');
                            setSelectedDelivery('CORREIOS');
                            dispatch(
                              updateDelivery({
                                tag: 'CORREIOS',
                                value: true,
                                is_suitcase_delivery: false,
                              })
                            );
                          }}
                          to="#"
                        >
                          <span className="text-nowrap">Correios</span>
                        </NavLink>
                      </NavItem>
                    </Col>
                  )}
                  {enabledDeliveryTypes.malinha && (
                    <Col className="px-0" xs={12} lg={3}>
                      <NavItem>
                        <NavLink
                          className={`new-layout-nav-pill card d-flex flex-column align-items-center h-fit-content w-100 ${
                            deliveryType === 'malinha' ? 'checked' : 'unchecked'
                          } font-size-14`}
                          onClick={() => {
                            if (deliveryTag === 'IFOOD_ENVIOS') {
                              toast.info(
                                'O iFood Envios não atende a modalidade Malinha. Selecione outra transportadora.'
                              );
                            }
                            setDeliveryType('malinha');
                            dispatch(
                              updateDelivery({
                                value: true,
                                is_suitcase_delivery: true,
                              })
                            );
                          }}
                          to="#"
                        >
                          <span className="text-nowrap">Malinha</span>
                        </NavLink>
                      </NavItem>
                    </Col>
                  )}
                </Row>
              </Nav>
            </Row>
          )}
        </CardBody>
      </Card>
      {deliveryType === 'malinha' ? (
        <Card className="card new-layout">
          <CardBody className="card-body new-layout">
            <Row className="w-100 mx-0 font-size-20 font-weight-500 text-gray-700 mb-2">
              Descrição dos produtos enviados
            </Row>
            <p>
              Anote as informações necessárias para ajudar a conferir quando os
              itens forem coletados.
            </p>

            <textarea
              name="description"
              rows={1}
              style={{
                outline: 'none',
                color: '#495057 !important',
                borderRadius: '0.15rem',
                fontSize: '14px',
                width: '100%',
                border: '1px solid #b9bbc6',
                padding: '0.8rem 0.6rem',
              }}
              ref={deliveryObservationRef}
              onChange={e => {
                setDeliveryObservation(e.target.value);
              }}
              value={deliveryObservation}
            />
          </CardBody>
        </Card>
      ) : null}
      {deliveryType && (
        <>
          <Card className="card new-layout">
            <CardBody className="card-body new-layout">
              <Row className="px-2">
                <Col lg={6} className="px-0 pr-lg-2">
                  <Row className="w-100 mx-0 font-size-20 font-weight-500 text-gray-700 mb-2">
                    {isForDelivery ? 'Loja para coleta' : 'Loja para retirada'}
                  </Row>
                  <Form ref={formStoreRef}>
                    <Row className="w-100 mx-0 mt-3 mb-4">
                      <FormGroup className="w-100">
                        <InputSelect
                          options={storesAvailable}
                          name="store_id"
                          onChange={setStoreSelectable}
                          isSearchable
                          placeholder="Nome da loja"
                          type="text"
                          value={selectedStore}
                          loadingMessage={() => 'Buscando lojas...'}
                          styles={storeSelectorStyles}
                        />
                      </FormGroup>
                    </Row>
                  </Form>
                </Col>
                {!routerEnabled &&
                  !['retirada', 'correios', 'correios_express'].includes(
                    deliveryType
                  ) && (
                    <Col lg={6} className="px-0 pr-lg-2">
                      <Row className="w-100 mx-0 font-size-20 font-weight-500 text-gray-700 mb-2">
                        Transportadora
                      </Row>
                      <Form ref={formVehicleRef}>
                        <Row className="w-100 mx-0 mt-3 mb-4">
                          <FormGroup className="w-100">
                            <InputSelect
                              name="delivery_service"
                              options={deliveryServiceOptions}
                              onChange={e => {
                                setSelectedDelivery(e.tag);
                                dispatch(
                                  updateDelivery({
                                    tag: e.tag,
                                    value: e.is_for_delivery,
                                  })
                                );
                              }}
                              value={
                                deliveryServiceOptions.find(
                                  deliveryService =>
                                    deliveryService.tag === deliveryTag
                                ) || null
                              }
                              placeholder="Selecione a Transportadora"
                              isSearchable
                              type="text"
                              styles={storeSelectorStyles}
                            />
                          </FormGroup>
                        </Row>
                      </Form>
                    </Col>
                  )}
                {routerEnabled &&
                  !['retirada', 'correios', 'correios_express'].includes(
                    deliveryType
                  ) && (
                    <Col lg={6} className="px-0 pr-lg-2">
                      <Row className="w-100 mx-0 font-size-20 font-weight-500 text-gray-700 mb-2">
                        Tipo de veículo
                      </Row>
                      <Form ref={formVehicleRef}>
                        <Row className="w-100 mx-0 mt-3 mb-4">
                          <FormGroup className="w-100">
                            <InputSelect
                              name="vehicle"
                              options={vehiclesAvailable}
                              onChange={e => {
                                dispatch(updateVehicleForDelivery(e.value));
                              }}
                              placeholder="Tipo de veículo"
                              isSearchable
                              type="text"
                              value={vehiclesAvailable.find(
                                ({ value }) => value === vehicle
                              )}
                              loadingMessage={() => 'Buscando veículos...'}
                              styles={storeSelectorStyles}
                            />
                          </FormGroup>
                        </Row>
                      </Form>
                    </Col>
                  )}
              </Row>
              <Row className="px-2">
                {!routerEnabled &&
                  isForDelivery &&
                  [
                    'BEE',
                    'UBER',
                    'LALAMOVE',
                    'ENTREGA99',
                    'IFOOD_ENVIOS',
                    'VIDDY_DELIVERIES',
                  ].includes(deliveryTag) && (
                    <Col lg={6} className="px-0 pr-lg-2">
                      <Row className="w-100 mx-0 font-size-20 font-weight-500 text-gray-700 mb-2">
                        Tipo de veículo
                      </Row>
                      <Form ref={formVehicleRef}>
                        <Row className="w-100 mx-0 mt-3 mb-4">
                          <FormGroup className="w-100">
                            <InputSelect
                              name="vehicle"
                              options={vehiclesAvailable}
                              onChange={e => {
                                dispatch(updateVehicleForDelivery(e.value));
                              }}
                              placeholder="Tipo de veículo"
                              isSearchable
                              type="text"
                              value={vehiclesAvailable.find(
                                ({ value }) => value === vehicle
                              )}
                              loadingMessage={() => 'Buscando veículos...'}
                              styles={storeSelectorStyles}
                            />
                          </FormGroup>
                        </Row>
                      </Form>
                    </Col>
                  )}
              </Row>
              {routerEnabled &&
                !['retirada', 'correios', 'correios_express'].includes(
                  deliveryType
                ) && (
                  <Row className="w-100 mx-0 mb-4">
                    <div
                      className="rounded px-3 py-2 w-100 d-flex align-items-center"
                      style={{ background: '#EEF2FF' }}
                    >
                      <i
                        className="las la-route font-size-20 mr-2"
                        style={{ color: '#4338CA' }}
                      />
                      <span
                        className="font-size-14"
                        style={{ color: '#3730A3' }}
                      >
                        Essa entrega é apoiada pelo Viddy e vamos te oferecer a
                        melhor opção para a sua região.
                      </span>
                    </div>
                  </Row>
                )}
              {isForDelivery && (deliveryType === 'correios' || deliveryTag) && (
                <>
                  <Row className="w-100 mx-0 font-size-20 font-weight-500 text-gray-700 mb-1">
                    Endereço de entrega
                  </Row>
                  <Row className="w-100 mx-0 mb-4">
                    <AddressForm
                      formRef={formRef}
                      initialData={client?.address}
                      handleSubmit={handleSubmitAddress}
                      submitBtnDisabled={done}
                      setSubmitBtnDisabled={setHandleDisabled}
                      sn={client?.address?.number === 'sn'}
                      iDontKnowCEP={iDontKnowCEP}
                      setIDontKnowCEP={setIDontKnowCEP}
                      selectedDelivery={selectedDelivery}
                      hideBeforeCEP
                    />
                  </Row>
                </>
              )}
            </CardBody>
          </Card>
          {deliveryType === 'correios' && (
            <Card className="card new-layout">
              <CardBody className="card-body new-layout">
                <Row className="w-100 mx-0 font-size-20 font-weight-500 text-gray-700 mb-1">
                  Dados de envio (pacote)
                </Row>
                <Row className="w-100 mx-0 mb-4">
                  <Col xs={12} className="px-0">
                    <p className="text-muted mb-0">
                      Selecione uma embalagem para preencher automaticamente as
                      medidas ou configure manualmente.
                    </p>
                  </Col>
                </Row>
                <Row className="mb-3">
                  {Object.entries(PACKAGES).map(([key, pkg]) => (
                    <Col lg={3} xs={12} className="mb-3" key={key}>
                      <button
                        type="button"
                        onClick={() => handleSelectPackage(key)}
                        className={`correios-package-card ${
                          packageType === key ? 'active' : ''
                        }`}
                      >
                        {packageType === key && (
                          <div className="correios-package-check">
                            <img src={iconCardSelected} alt="Selecionado" />
                          </div>
                        )}

                        <div className="correios-package-icon">
                          <img src={pkg.icon} alt={pkg.label} />
                        </div>

                        <div className="mt-3">
                          <h5 className="font-weight-600 mb-1">{pkg.label}</h5>

                          {key !== 'CUSTOM' ? (
                            <>
                              <p className="text-muted font-size-12 mb-1">
                                {pkg.width}×{pkg.length}×{pkg.height} cm
                              </p>
                              <p className="text-muted font-size-12 mb-2">
                                {pkg.maxWeightLabel}
                              </p>
                            </>
                          ) : (
                            <>
                              <p className="text-muted font-size-12 mb-1">
                                ---×---×--- cm
                              </p>
                              <p className="text-muted font-size-12 mb-2">
                                {pkg.maxWeightLabel}
                              </p>
                            </>
                          )}

                          <div className="correios-package-separator" />

                          <p className="font-size-10 mb-0">
                            <span className="correios-package-ideal-label">
                              Ideal para:
                            </span>{' '}
                            <span className="correios-package-ideal-text">
                              {pkg.description}
                            </span>
                          </p>
                        </div>
                      </button>
                    </Col>
                  ))}
                </Row>
                <div className="correios-dimensions-wrapper mt-3">
                  <Row className="w-100 mx-0">
                    <Col lg={8} xs={12} className="correios-dimensions-left">
                      <Row className="w-100 mx-0 font-size-16 font-weight-500 text-gray-700 mb-3">
                        Dimensões e peso
                      </Row>

                      <Row className="correios-dimensions-input-row">
                        <Col lg={3} xs={12} className="correios-dimension-col">
                          <FormGroup>
                            <label className="font-size-12">Largura (L)</label>
                            <input
                              type="number"
                              disabled={packageType !== 'CUSTOM'}
                              className={`form-control ${
                                dimensionErrors.width ? 'is-invalid' : ''
                              }`}
                              value={dimensions.width}
                              onChange={e =>
                                setDimensions({
                                  ...dimensions,
                                  width: e.target.value,
                                })
                              }
                            />
                            {dimensionErrors.width && (
                              <small className="text-danger">
                                {dimensionErrors.width}
                              </small>
                            )}
                          </FormGroup>
                        </Col>

                        <Col lg={3} xs={12} className="correios-dimension-col">
                          <FormGroup>
                            <label className="font-size-12">
                              Comprimento (C)
                            </label>
                            <input
                              type="number"
                              disabled={packageType !== 'CUSTOM'}
                              className={`form-control ${
                                dimensionErrors.length ? 'is-invalid' : ''
                              }`}
                              value={dimensions.length}
                              onChange={e =>
                                setDimensions({
                                  ...dimensions,
                                  length: e.target.value,
                                })
                              }
                            />
                            {dimensionErrors.length && (
                              <small className="text-danger">
                                {dimensionErrors.length}
                              </small>
                            )}
                          </FormGroup>
                        </Col>

                        <Col lg={3} xs={12} className="correios-dimension-col">
                          <FormGroup>
                            <label className="font-size-12">Altura (A)</label>
                            <input
                              type="number"
                              disabled={packageType !== 'CUSTOM'}
                              className={`form-control ${
                                dimensionErrors.height ? 'is-invalid' : ''
                              }`}
                              value={dimensions.height}
                              onChange={e =>
                                setDimensions({
                                  ...dimensions,
                                  height: e.target.value,
                                })
                              }
                            />
                            {dimensionErrors.height && (
                              <small className="text-danger">
                                {dimensionErrors.height}
                              </small>
                            )}
                          </FormGroup>
                        </Col>

                        <Col lg={3} xs={12} className="correios-dimension-col">
                          <FormGroup>
                            <label className="font-size-12">Peso (kg)</label>
                            <input
                              type="number"
                              disabled={packageType !== 'CUSTOM'}
                              className={`form-control ${
                                dimensionErrors.weight ? 'is-invalid' : ''
                              }`}
                              value={dimensions.weight}
                              onChange={e =>
                                setDimensions({
                                  ...dimensions,
                                  weight: e.target.value,
                                })
                              }
                            />
                            {dimensionErrors.weight && (
                              <small className="text-danger">
                                {dimensionErrors.weight}
                              </small>
                            )}
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col xs={12}>
                          <p className="correios-dimensions-helper font-size-12 mt-1 mb-0">
                            <img
                              src={iconTextBox}
                              alt=""
                              className="correios-helper-icon"
                            />
                            Valores preenchidos automaticamente com base na
                            embalagem selecionada.
                          </p>
                        </Col>
                      </Row>
                    </Col>

                    <Col
                      lg={4}
                      xs={12}
                      className="correios-dimensions-right d-flex align-items-center justify-content-center mt-4 mt-lg-0"
                    >
                      <img
                        src={dimensionsImg}
                        alt="Ilustração das dimensões largura, comprimento e altura"
                        className="correios-dimensions-illustration"
                      />
                    </Col>
                  </Row>
                </div>
              </CardBody>
            </Card>
          )}
        </>
      )}

      <Row className="w-100 mx-0 mb-4">
        <Col className="w-100 px-0">
          <Button
            color="primary"
            size="lg"
            type="submit"
            className="float-right w-lg-25 new-layout-btn main"
            disabled={handleDisabled}
            onClick={
              userStore?.value === selectedStore?.value
                ? handleSubmit
                : () => setShowDefaultStoreAlert(true)
            }
          >
            {btnLabel}
          </Button>
        </Col>
      </Row>
      <DefaultStoreAlert
        setShow={setShowDefaultStoreAlert}
        show={showDefaultStoreAlert}
        submit={handleSubmit}
      />
    </>
  );
};

export default DeliveryForm;
